import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogOut, Search, Trash2, RefreshCw, Users, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/registrations");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Nie udało się pobrać zapisów.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("mk_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("mk_admin_token");
    localStorage.removeItem("mk_admin_email");
    navigate("/admin/login");
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć ten zapis?")) return;
    try {
      await api.delete(`/admin/registrations/${id}`);
      setItems(items.filter((i) => i.id !== id));
      toast.success("Usunięto");
    } catch {
      toast.error("Nie udało się usunąć.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.phone?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const email = localStorage.getItem("mk_admin_email");

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E8E1D9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl">
            mamy<span className="text-[#E6007A]">!</span>kongres
            <span className="text-sm text-[#5C534D] font-body ml-3">admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-sm text-[#5C534D]">{email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E1D9] px-4 py-2 text-sm hover:border-[#E6007A] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-4xl md:text-5xl">Zapisy</h1>
            <p className="text-[#5C534D] mt-2">Lista uczestniczek kongresu — 22 maja 2027.</p>
          </div>
          <div className="flex gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white border border-[#E8E1D9] px-5 py-3 min-w-[140px]">
                <Users className="w-4 h-4 text-[#E6007A] mb-1" />
                <div className="font-display text-2xl">{items.length}</div>
                <div className="text-xs text-[#5C534D] uppercase tracking-wider">łącznie</div>
              </div>
              <div className="rounded-2xl bg-white border border-[#E8E1D9] px-5 py-3 min-w-[140px]">
                <Mail className="w-4 h-4 text-[#E6007A] mb-1" />
                <div className="font-display text-2xl">{items.filter(i => i.consent).length}</div>
                <div className="text-xs text-[#5C534D] uppercase tracking-wider">zgody rodo</div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-[#E8E1D9] overflow-hidden">
          <div className="p-4 border-b border-[#E8E1D9] flex flex-wrap gap-3 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#5C534D] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj po imieniu, mailu, telefonie..."
                data-testid="admin-search"
                className="w-full rounded-xl border border-[#E8E1D9] pl-10 pr-4 py-2.5 focus:border-[#E6007A] outline-none text-sm"
              />
            </div>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E1D9] px-4 py-2 text-sm hover:border-[#E6007A] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Odśwież
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#5C534D] bg-[#F9F8F6]">
                <tr>
                  <th className="px-6 py-4">#</th>
                  <th className="px-6 py-4">Imię i nazwisko</th>
                  <th className="px-6 py-4">Email</th>
                  <th className="px-6 py-4">Telefon</th>
                  <th className="px-6 py-4">Data zapisu</th>
                  <th className="px-6 py-4">RODO</th>
                  <th className="px-6 py-4 text-right">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-[#5C534D]">Ładowanie...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-[#5C534D]">Brak zapisów.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={r.id} className="border-t border-[#E8E1D9] hover:bg-[#FDFBF7] transition-colors" data-testid={`admin-row-${r.id}`}>
                      <td className="px-6 py-4 text-[#5C534D]">{i + 1}</td>
                      <td className="px-6 py-4 font-medium">{r.name}</td>
                      <td className="px-6 py-4"><a href={`mailto:${r.email}`} className="hover:text-[#E6007A]">{r.email}</a></td>
                      <td className="px-6 py-4">{r.phone}</td>
                      <td className="px-6 py-4 text-[#5C534D]">
                        {new Date(r.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs ${r.consent ? "bg-[#E6007A]/10 text-[#E6007A]" : "bg-[#F4EFE6] text-[#5C534D]"}`}>
                          {r.consent ? "TAK" : "NIE"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => remove(r.id)}
                          className="inline-flex items-center gap-1 text-[#5C534D] hover:text-red-500 transition-colors"
                          data-testid={`admin-delete-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
